import React, { useState } from 'react';
import styled from 'styled-components';
import { Phone, Mail, Clock, MapPin, MessageCircle, Send, User, Headphones, FileText, Upload } from 'lucide-react';
import ContentLayout from '../../../Common/Component/ContentLayout';

const Container = styled.div`
  width: 100%;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactSection = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ContactGrid = styled.div`
  display: grid;
  gap: 20px;
`;

const ContactCard = styled.div`
  padding: 20px;
  background: #f9fafb;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 15px;
`;

const ContactIcon = styled.div`
  width: 48px;
  height: 48px;
  background: #3b82f6;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const ContactInfo = styled.div`
  flex: 1;
`;

const ContactLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 5px;
`;

const ContactValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 3px;
`;

const ContactDescription = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const InquiryForm = styled.form`
  display: grid;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const FormInput = styled.input`
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const FormSelect = styled.select`
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const FormTextarea = styled.textarea`
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  min-height: 120px;
  resize: vertical;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const SubmitButton = styled.button`
  padding: 15px 30px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s;
  
  &:hover {
    background: #2563eb;
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const HoursGrid = styled.div`
  display: grid;
  gap: 10px;
`;

const HoursRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const HoursDay = styled.span`
  font-weight: 500;
  color: #374151;
`;

const HoursTime = styled.span`
  color: #6b7280;
  font-size: 14px;
`;

const QuickActions = styled.div`
  background: white;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  margin-bottom: 30px;
`;

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    border-color: #3b82f6;
    transform: translateY(-1px);
  }
`;

const ActionIcon = styled.div`
  width: 32px;
  height: 32px;
  background: #3b82f6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const ActionText = styled.div`
  text-align: left;
`;

const ActionTitle = styled.div`
  font-weight: 600;
  color: #1f2937;
  font-size: 14px;
`;

const ActionDescription = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    category: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // 실제로는 API 호출
    setTimeout(() => {
      alert('문의가 접수되었습니다. 빠른 시일 내에 답변드리겠습니다.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        category: '',
        subject: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <ContentLayout 
      title="1:1 문의"
      subtitle="궁금한 사항이나 불편한 점이 있으시면 언제든지 연락해주세요. 최선을 다해 도움드리겠습니다."
    >
      <Container>

      <QuickActions>
        <SectionTitle>
          <Headphones size={20} />
          빠른 문의
        </SectionTitle>
        <ActionGrid>
          <ActionButton>
            <ActionIcon>
              <Phone size={16} />
            </ActionIcon>
            <ActionText>
              <ActionTitle>전화 상담</ActionTitle>
              <ActionDescription>1588-1234</ActionDescription>
            </ActionText>
          </ActionButton>
          <ActionButton>
            <ActionIcon>
              <MessageCircle size={16} />
            </ActionIcon>
            <ActionText>
              <ActionTitle>카카오톡</ActionTitle>
              <ActionDescription>@antsmarket</ActionDescription>
            </ActionText>
          </ActionButton>
          <ActionButton>
            <ActionIcon>
              <Mail size={16} />
            </ActionIcon>
            <ActionText>
              <ActionTitle>이메일</ActionTitle>
              <ActionDescription>support@antsmarket.com</ActionDescription>
            </ActionText>
          </ActionButton>
        </ActionGrid>
      </QuickActions>

      <ContentGrid>
        <ContactSection>
          <SectionTitle>
            <Phone size={20} />
            연락처 정보
          </SectionTitle>
          
          <ContactGrid>
            <ContactCard>
              <ContactIcon>
                <Phone size={20} />
              </ContactIcon>
              <ContactInfo>
                <ContactLabel>고객센터</ContactLabel>
                <ContactValue>1588-1234</ContactValue>
                <ContactDescription>평일 09:00 - 18:00</ContactDescription>
              </ContactInfo>
            </ContactCard>

            <ContactCard>
              <ContactIcon>
                <Mail size={20} />
              </ContactIcon>
              <ContactInfo>
                <ContactLabel>이메일</ContactLabel>
                <ContactValue>support@antsmarket.com</ContactValue>
                <ContactDescription>24시간 접수 가능</ContactDescription>
              </ContactInfo>
            </ContactCard>

            <ContactCard>
              <ContactIcon>
                <MapPin size={20} />
              </ContactIcon>
              <ContactInfo>
                <ContactLabel>본사 주소</ContactLabel>
                <ContactValue>서울시 강남구 테헤란로 123</ContactValue>
                <ContactDescription>AntsMarket 본사 10층</ContactDescription>
              </ContactInfo>
            </ContactCard>
          </ContactGrid>

          <div style={{ marginTop: '25px' }}>
            <ContactLabel style={{ marginBottom: '15px', display: 'block' }}>
              운영 시간
            </ContactLabel>
            <HoursGrid>
              <HoursRow>
                <HoursDay>평일</HoursDay>
                <HoursTime>09:00 - 18:00</HoursTime>
              </HoursRow>
              <HoursRow>
                <HoursDay>토요일</HoursDay>
                <HoursTime>10:00 - 15:00</HoursTime>
              </HoursRow>
              <HoursRow>
                <HoursDay>일요일/공휴일</HoursDay>
                <HoursTime>휴무</HoursTime>
              </HoursRow>
              <HoursRow>
                <HoursDay>점심시간</HoursDay>
                <HoursTime>12:00 - 13:00</HoursTime>
              </HoursRow>
            </HoursGrid>
          </div>
        </ContactSection>

        <ContactSection>
          <SectionTitle>
            <User size={20} />
            1:1 문의하기
          </SectionTitle>
          
          <InquiryForm onSubmit={handleSubmit}>
            <FormGroup>
              <FormLabel>이름 *</FormLabel>
              <FormInput
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="이름을 입력하세요"
                required
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>이메일 *</FormLabel>
              <FormInput
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="답변 받을 이메일을 입력하세요"
                required
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>연락처</FormLabel>
              <FormInput
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="연락처를 입력하세요"
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>문의 유형 *</FormLabel>
              <FormSelect
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                required
              >
                <option value="">문의 유형을 선택하세요</option>
                <option value="order">주문/결제</option>
                <option value="delivery">배송</option>
                <option value="return">교환/반품</option>
                <option value="product">상품</option>
                <option value="account">회원/계정</option>
                <option value="technical">기술지원</option>
                <option value="other">기타</option>
              </FormSelect>
            </FormGroup>

            <FormGroup>
              <FormLabel>제목 *</FormLabel>
              <FormInput
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                placeholder="문의 제목을 입력하세요"
                required
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>문의 내용 *</FormLabel>
              <FormTextarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="문의하실 내용을 자세히 작성해주세요"
                required
              />
            </FormGroup>

            <SubmitButton type="submit" disabled={isSubmitting}>
              <Send size={16} />
              {isSubmitting ? '전송 중...' : '문의하기'}
            </SubmitButton>
          </InquiryForm>
        </ContactSection>
      </ContentGrid>
      </Container>
    </ContentLayout>
  );
}

export default Contact;